/**
 * Beleg-PDFs erzeugen -- über den EIGENEN Server (Ziel) oder den JasperReports-Dienst (Übergang).
 *
 * Zwei Betriebsarten, gesteuert über Build-Umgebungsvariablen:
 *
 *   VITE_RENDER_TARGET=server    Eigener Windows-Server: POST /belege/render/{vorlage} auf DERSELBEN
 *                                Herkunft. Kein Token -- es gilt die angemeldete Sitzung (Cookie).
 *                                Der ApplicationHost reicht an die ReportRuntime (RDL) weiter.
 *
 *   VITE_RENDER_URL=https://…    Übergangsbetrieb Cloud: externer JasperReports-Dienst mit
 *   VITE_RENDER_TOKEN=…          Bearer-Token.
 *
 * Ist nichts gesetzt, ist die Funktion inaktiv (reportServerConfigured() = false) und der PDF-Knopf
 * erscheint gar nicht.
 *
 * ÜBERGANG: Der Jasper-Zweig ist bewusst abgegrenzt und faellt weg, sobald die Cloud abgeschaltet
 * wird -- dann bleiben nur die drei Zeilen des Server-Zweigs. Er traegt auch die alte Schwaeche:
 * VITE_*-Variablen landen im Client-Bundle, der Token ist also lesbar. Im Server-Betrieb gibt es
 * keinen Token mehr, weil der Renderer nur ueber die Rueckschleife des Hosts erreichbar ist.
 */

/** 'server' = eigener Server (gleiche Herkunft, Sitzung); sonst der externe Jasper-Dienst. */
const RENDER_TARGET = ((import.meta.env.VITE_RENDER_TARGET as string | undefined) ?? '').trim();
const RENDER_URL = ((import.meta.env.VITE_RENDER_URL as string | undefined) ?? '').replace(/\/+$/, '');
const RENDER_TOKEN = (import.meta.env.VITE_RENDER_TOKEN as string | undefined) ?? '';

const AUF_EIGENEM_SERVER = RENDER_TARGET === 'server';

/**
 * Woher die Sitzungsmarke kommt.
 *
 * Der Beleg-Endpunkt hing bis 2026-09-06 ALLEIN am Sitzungscookie -- als
 * einziger Weg im ganzen Produkt. Das ist im LAN eines Kunden ein
 * Funktionsausfall: der Host setzt das Cookie mit `Secure`, lauscht aber auf
 * HTTP 5280. Ein Secure-Cookie erreicht `http://server:5280` von einem
 * Client-PC nie. Anmelden und alle 187 RPC-Aufrufe funktionierten trotzdem,
 * weil die Supabase-Bibliothek eine Bearer-Marke mitschickt -- nur der
 * PDF-Knopf antwortete 401 „Nicht angemeldet", und zwar ausschliesslich von
 * einem zweiten Rechner aus. Auf dem Server selbst (localhost) fiel es nicht auf.
 *
 * Der Host akzeptiert beide Wege ohnehin (Aufrufer.ErmittelnAsync: erst Cookie,
 * dann Bearer). Also nimmt der Beleg-Endpunkt jetzt denselben Weg wie alles
 * andere -- eine Anmeldung, ein Mechanismus. Das Cookie bleibt zusaetzlich
 * erlaubt und gewinnt weiterhin, wo es ankommt.
 */
let markenLieferant: (() => Promise<string | null>) | null = null;

/**
 * Die Quelle der Sitzungsmarke hinterlegen. Wird beim Aufbau der
 * Supabase-Dienste gesetzt; im Demobetrieb bleibt sie leer und der PDF-Knopf
 * erscheint ohnehin nicht.
 */
export function setzeMarkenLieferant(lieferant: (() => Promise<string | null>) | null): void {
  markenLieferant = lieferant;
}

async function sitzungsMarke(): Promise<string | null> {
  if (!markenLieferant) return null;
  try {
    return await markenLieferant();
  } catch {
    // Keine Marke ist besser als ein Absturz: der Host lehnt dann sauber mit
    // 401 ab, statt dass der Knopf ohne Meldung nichts tut.
    return null;
  }
}

export interface ReportPayload {
  readonly params: Record<string, string>;
  readonly rows: ReadonlyArray<Record<string, string>>;
}

/** Kann überhaupt gerendert werden? Nur dann den PDF-Knopf zeigen. */
export function reportServerConfigured(): boolean {
  return AUF_EIGENEM_SERVER || RENDER_URL !== '';
}

/** Wohin die Anfrage geht -- für Fehlermeldungen und Tests nachvollziehbar. */
export function reportEndpoint(template: string): string {
  const vorlage = encodeURIComponent(template);
  return AUF_EIGENEM_SERVER ? `/belege/render/${vorlage}` : `${RENDER_URL}/render/${vorlage}`;
}

/** Vorlage füllen lassen und das PDF als Blob zurückgeben. Wirft bei Fehler (mit Klartext des Dienstes). */
export async function fetchReportPdf(template: string, payload: ReportPayload): Promise<Blob> {
  // Auf dem eigenen Server: Cookie UND Marke. Das Cookie ist der bessere Weg
  // (ein Skript im Browser kann es nicht lesen) und gewinnt beim Host, wo es
  // ankommt; die Marke ist der Weg, der auch über HTTP funktioniert.
  const marke = AUF_EIGENEM_SERVER ? await sitzungsMarke() : null;

  const response = await fetch(reportEndpoint(template), {
    method: 'POST',
    credentials: AUF_EIGENEM_SERVER ? 'same-origin' : 'omit',
    headers: {
      'Content-Type': 'application/json',
      ...(AUF_EIGENEM_SERVER
        ? (marke ? { Authorization: `Bearer ${marke}` } : {})
        : (RENDER_TOKEN ? { Authorization: `Bearer ${RENDER_TOKEN}` } : {})),
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(`Berichtsdienst antwortete ${response.status}${text ? `: ${text.slice(0, 300)}` : ''}`);
  }
  return response.blob();
}

/** Das PDF in einem neuen Tab öffnen (Blob-URL, nach einer Minute wieder freigeben). */
export function openPdfBlob(blob: Blob): void {
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank', 'noopener');
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
